import * as React from 'react';
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useDialog } from '../../../utils/dialog';
import { surface as surfaceCls } from './Popover.css';

export type PopoverPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'right';

/**
 * The ARIA roles a Popover surface can take. `group` is the default —
 * a non-modal labelled region that does not trap focus (Tab moves on
 * through the page). `dialog` opts into modal semantics: focus is
 * trapped + moved in + restored on close via `useDialog`. `menu` /
 * `listbox` are for the composed `Menu` / Select-style variants, which
 * manage their own roving focus and arrow keys.
 */
export type PopoverRole = 'group' | 'dialog' | 'menu' | 'listbox';

/**
 * `aria-haspopup` value the trigger advertises. Mirrors the surface role
 * for `dialog` / `menu` / `listbox`; a `group` surface advertises the
 * generic `true` (there is no `aria-haspopup="group"`). Narrowed to the
 * values Popover actually emits — it never sets `tree` / `grid`.
 */
export type PopoverHasPopup = true | 'dialog' | 'menu' | 'listbox';

export interface PopoverProps {
  /** Trigger element. Must accept a ref + onClick + aria-* props. Any
   * element with React's standard `aria-haspopup` is assignable — Popover
   * itself only ever *emits* the narrowed `PopoverHasPopup` (see line ~245),
   * but composed triggers (e.g. `Menu`) carry the wider DOM type. */
  trigger: React.ReactElement<{
    ref?: React.Ref<HTMLElement>;
    onClick?: React.MouseEventHandler<HTMLElement>;
    'aria-expanded'?: boolean;
    'aria-controls'?: string;
    'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
  }>;
  children: React.ReactNode;
  placement?: PopoverPlacement;
  /** Pixel offset between trigger edge and popover edge. */
  offset?: number;
  /** Controlled open state. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * ARIA role for the popover content. Defaults to `"group"` (a
   * non-modal region — Tab moves through, no focus trap). Pass
   * `"dialog"` to opt into modal semantics (focus trap + move + restore
   * via `useDialog`). `"menu"` / `"listbox"` are for the composed
   * `Menu` / Select variants, which own their focus model.
   */
  role?: PopoverRole;
}

interface Position {
  top: number;
  left: number;
}

function computePosition(
  triggerRect: DOMRect,
  popoverRect: { width: number; height: number },
  placement: PopoverPlacement,
  offset: number,
): Position {
  const { top, bottom, left, right, height, width } = triggerRect;
  const { width: pw, height: ph } = popoverRect;

  switch (placement) {
    case 'top':
      return { top: top - ph - offset, left: left + width / 2 - pw / 2 };
    case 'top-start':
      return { top: top - ph - offset, left };
    case 'top-end':
      return { top: top - ph - offset, left: right - pw };
    case 'bottom-start':
      return { top: bottom + offset, left };
    case 'bottom-end':
      return { top: bottom + offset, left: right - pw };
    case 'left':
      return { top: top + height / 2 - ph / 2, left: left - pw - offset };
    case 'right':
      return { top: top + height / 2 - ph / 2, left: right + offset };
    case 'bottom':
    default:
      return { top: bottom + offset, left: left + width / 2 - pw / 2 };
  }
}

/**
 * Popover — portal-rendered floating surface anchored to a trigger
 * element. Bring-your-own trigger (any element that accepts `ref` +
 * `onClick` + `aria-*` props is cloned and wired up automatically).
 *
 * - Controlled (`open` + `onOpenChange`) or uncontrolled (`defaultOpen`).
 * - Auto-positions per `placement`; reposition on scroll/resize.
 * - `role="group"` by default — a **non-modal** floating region. Focus is
 *   *not* trapped (Tab moves on through the page); outside-click + Escape
 *   close, and Escape returns focus to the trigger.
 * - Pass `role="dialog"` to opt into **modal** semantics. The surface then
 *   delegates to `useDialog`: focus moves into the surface on open, Tab /
 *   Shift+Tab are trapped, background scroll is locked, Escape closes, and
 *   focus is restored to the previously-focused element on close —
 *   matching the `aria-modal` + `aria-haspopup="dialog"` it advertises.
 * - `role="menu"` / `"listbox"` are for the composed `Menu` / Select-style
 *   variants, which own their roving-focus + arrow-key model; the
 *   trigger's `aria-haspopup` mirrors the role.
 */
export function Popover({
  trigger,
  children,
  placement = 'bottom',
  offset = 6,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  role = 'group',
}: PopoverProps) {
  const [internal, setInternal] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internal;
  const isDialog = role === 'dialog';

  const triggerRef = useRef<HTMLElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const popoverId = useId();

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternal(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  // Modal `role="dialog"` surfaces delegate focus trap + move + restore +
  // scroll-lock + Escape to the shared `useDialog` util (the same hook
  // Modal uses) so the advertised modal semantics actually hold. The hook
  // no-ops while `open` is false / for non-dialog surfaces (guarded by
  // `open && isDialog`). `useDialog` itself restores focus to the trigger
  // on close, so the manual `triggerRef.focus()` in the non-dialog Escape
  // path below is not duplicated here.
  useDialog({
    open: open && isDialog,
    containerRef: surfaceRef,
    onClose: () => setOpen(false),
    // A pointer-opened popover may leave focus on <body>, so restore to the
    // trigger explicitly rather than relying on previously-focused element.
    returnFocusRef: triggerRef,
  });

  // Reposition on open + on scroll/resize while open.
  const reposition = useCallback(() => {
    if (!triggerRef.current || !surfaceRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverRect = {
      width: surfaceRef.current.offsetWidth,
      height: surfaceRef.current.offsetHeight,
    };
    setPosition(computePosition(triggerRect, popoverRect, placement, offset));
  }, [placement, offset]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    // Wait one frame so the surface is in the DOM before measuring.
    const id = requestAnimationFrame(reposition);
    const onWindow = () => reposition();
    window.addEventListener('resize', onWindow);
    window.addEventListener('scroll', onWindow, true);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', onWindow);
      window.removeEventListener('scroll', onWindow, true);
    };
  }, [open, reposition]);

  // Outside-click closes for every role. Escape is handled here only for
  // NON-dialog surfaces — for `role="dialog"`, `useDialog` already owns
  // Escape (and focus restoration), so wiring it here too would close
  // twice and fight the hook's restore.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (surfaceRef.current?.contains(t) || triggerRef.current?.contains(t)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, setOpen]);

  useEffect(() => {
    if (!open || isDialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, isDialog, setOpen]);

  const triggerNode = isValidElement(trigger)
    ? cloneElement(trigger, {
        ref: (node: HTMLElement | null) => {
          triggerRef.current = node;
          // forward to original ref if any
          const original = (trigger as { ref?: React.Ref<HTMLElement> | null }).ref;
          if (typeof original === 'function') original(node);
          else if (original && typeof original === 'object')
            (original as { current: HTMLElement | null }).current = node;
        },
        onClick: (e: React.MouseEvent<HTMLElement>) => {
          trigger.props.onClick?.(e);
          setOpen(!open);
        },
        'aria-expanded': open,
        'aria-controls': popoverId,
        // No `aria-haspopup="group"` exists; a generic region advertises
        // the boolean `true`. Modal / menu / listbox surfaces mirror.
        'aria-haspopup': role === 'group' ? true : role,
      })
    : trigger;

  return (
    <>
      {triggerNode}
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={surfaceRef}
            id={popoverId}
            role={role}
            aria-modal={isDialog || undefined}
            tabIndex={-1}
            className={surfaceCls}
            style={{
              top: `${position?.top ?? 0}px`,
              left: `${position?.left ?? 0}px`,
              opacity: position ? 1 : 0,
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
Popover.displayName = 'Popover';
